import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {ReactNode} from 'react';

import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import ProgressBar from 'ui-library/bs/ProgressBar';
import formatEstimateMs from 'util/formatEstimateMs';
import {capitalize} from 'util/NameUtil';

function ToastProgressText(props: {totalTimeMs?: number; progress?: number}): JSX.Element | null {
  if (props.totalTimeMs == null || props.progress == null || props.progress === 100) {
    return null;
  }
  if (props.progress === TrayTaskConstants.INDETERMINATE_PROGRESS) {
    return <i>Starting</i>;
  }
  const remainingTimeMs = (props.totalTimeMs * (100 - props.progress)) / 100;
  const message = `${capitalize(formatEstimateMs(remainingTimeMs))} remaining`;
  return (
    <>
      {props.progress.toFixed(0)}% — <i>{message}</i>
    </>
  );
}

/**
 * @param props.progress - Progress as a percentage. If the task hasn't reported
 *  progress yet, use TrayTaskConstants.INDETERMINATE_PROGRESS to show "Starting".
 *
 * @param props.totalTimeMs - If specified, shows percentage and remaining time
 *   under the progress bar, e.g. "93% — 2 minutes remaining"
 *
 *   For this to be accurate when used with TaskRunner, `PollOption.taskRequestDone`
 *   must be set to 0 (otherwise, 0% progress reported by the backend will show
 *   up here as 25%).
 *
 *   NOTE: The percentage will be redundant with that shown via TrayTaskActions,
 *   so the progress set via TrayTaskActions should be set to
 *   `TrayTaskConstants.HIDE_PROGRESS` to cede rendering to this component.
 */
export default function ToastMessage(props: {
  header?: ReactNode;
  body?: ReactNode;
  progress?: number;
  totalTimeMs?: number;
}): JSX.Element {
  return (
    <>
      {props.header && <div className={css(styles.toastHeader)}>{props.header}</div>}
      {props.body && <div>{props.body}</div>}
      {props.progress != null && props.progress < 100 && (
        <>
          <ProgressBar
            className={css(styles.progressBar)}
            striped={true}
            active={true}
            value={
              props.progress === TrayTaskConstants.INDETERMINATE_PROGRESS
                ? // If the task hasn't started, show an empty progress bar.
                  1
                : // If the task has started, show at least some progress.
                  Math.max(2, props.progress)
            }
            type="success"
          />
        </>
      )}
      <div>
        <ToastProgressText totalTimeMs={props.totalTimeMs} progress={props.progress} />
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  toastHeader: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  progressBar: {
    marginTop: 10,
    width: '275px',
    marginBottom: 10,
  },
});
